import { useEffect, useState } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import Autocomplete from '@/components/ui/Autocomplete/Autocomplete';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import FieldWrapper from '@/components/ui/FieldWrapper/FieldWrapper';
import TextField from '@/components/ui/TextField/TextField';
import Typography from '@/components/ui/Typography/Typography';
import VehicleColorDot from '@/components/business/VehicleColorDot/VehicleColorDot';
import { VEHICLE_COLORS } from '@/helpers/vehicleOptions';
import type {
    EditDetailsFormValues,
    EditDetailsModalProps,
} from './IEditDetailsModal';
import { styles } from './styles';

const buildInitial = (job: EditDetailsModalProps['job']): EditDetailsFormValues => {
    const odo = job.vehicle?.odometer;
    return {
        stockNumber: job.vehicle?.stock_number ?? '',
        poNumber: job.po_number ?? '',
        color: job.vehicle?.color ?? '',
        odometer: odo?.value != null ? String(odo.value) : '',
        odometerUnit: (odo?.unit as 'mi' | 'km') ?? 'mi',
    };
};

const EditDetailsModal = ({
    open,
    job,
    onClose,
    onSave,
}: EditDetailsModalProps) => {
    const [form, setForm] = useState<EditDetailsFormValues>(() => buildInitial(job));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm(buildInitial(job));
        }
    }, [open, job]);

    const handleSubmit = async () => {
        setSaving(true);
        try {
            await onSave(form);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Job Details</DialogTitle>
            <DialogContent sx={styles.content}>
                <FieldWrapper filled={Boolean(form.color)} required>
                    <Autocomplete<string>
                        options={VEHICLE_COLORS}
                        value={form.color || null}
                        onChange={(_, value) =>
                            setForm({ ...form, color: value ?? '' })
                        }
                        label="Color"
                        required
                        renderOption={(props, option) => {
                            const { key, ...rest } = props as typeof props & {
                                key?: React.Key;
                            };
                            return (
                                <li key={key} {...rest}>
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <VehicleColorDot color={option} />
                                        <Box component="span">{option}</Box>
                                    </Box>
                                </li>
                            );
                        }}
                        startAdornment={
                            form.color ? (
                                <VehicleColorDot color={form.color} />
                            ) : null
                        }
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(form.odometer)}>
                    <TextField
                        fullWidth
                        label="Odometer"
                        type="number"
                        value={form.odometer}
                        onChange={(e) =>
                            setForm({ ...form, odometer: e.target.value })
                        }
                    />
                </FieldWrapper>
                <Box>
                    <Typography sx={styles.sectionLabel}>Odometer Unit</Typography>
                    <ToggleButtonGroup
                        value={form.odometerUnit}
                        exclusive
                        size="small"
                        onChange={(_, val) =>
                            val && setForm({ ...form, odometerUnit: val })
                        }
                    >
                        <ToggleButton value="mi">Miles</ToggleButton>
                        <ToggleButton value="km">Kilometers</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <FieldWrapper filled={Boolean(form.stockNumber)}>
                    <TextField
                        fullWidth
                        label="Stock/RO"
                        value={form.stockNumber}
                        onChange={(e) =>
                            setForm({ ...form, stockNumber: e.target.value })
                        }
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(form.poNumber)}>
                    <TextField
                        fullWidth
                        label="PO Number"
                        value={form.poNumber}
                        onChange={(e) =>
                            setForm({ ...form, poNumber: e.target.value })
                        }
                    />
                </FieldWrapper>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="secondary"
                    disabled={saving}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditDetailsModal;
